import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "347",
  "slug": "why-some-countries-drive-on-the-left",
  "title": "Why Do Some Countries Drive on the Left?",
  "excerpt": "About a third of the world drives on the left. The reasons go back to swords, freight wagons, the British Empire, and one dramatic morning in Sweden.",
  "date": "2026-06-04",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1520594923568-1b5d82587f86?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxoaWdod2F5JTIwdHJhZmZpYyUyMGFlcmlhbHxlbnwxfDB8fHwxNzgwNTYwNDc4fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Roughly a third of countries drive on the left, most of them former British territories. The split traces back to how people traveled before cars — keeping your sword arm toward oncoming strangers — and later to wagon design and the reach of the British Empire.",
  "content": [
    {
      "type": "paragraph",
      "text": "Step off a plane in London, Tokyo, or Sydney and the first thing that feels wrong is the traffic coming at you from the 'other' side. Around a third of the world drives on the left and the rest on the right. It looks arbitrary, but there is a long history behind which side a country picked."
    },
    {
      "type": "heading",
      "text": "It Started Before Cars"
    },
    {
      "type": "paragraph",
      "text": "For most of recorded history, keeping to the left was the natural choice. Most people are right-handed, and a traveler on horseback or on foot wanted his dominant hand free to greet — or fight — anyone approaching. Passing on the left kept your right, your sword arm, between you and a potential threat. Mounting a horse was also easier from the left and safer to do at the roadside rather than in the middle of traffic. Left-hand travel was effectively the world's default."
    },
    {
      "type": "heading",
      "text": "How the Right Side Took Over"
    },
    {
      "type": "paragraph",
      "text": "The switch began with freight. In late-1700s France and the United States, teamsters hauled goods with large wagons pulled by several pairs of horses, and there was no driver's seat. The driver sat on the rear-left horse so his right hand was free to work the whip. From that position he wanted to pass oncoming wagons on his left, which meant keeping his own wagon to the right of the road. Post-revolutionary France adopted right-hand travel, and Napoleon's conquests spread the practice across much of continental Europe."
    },
    {
      "type": "heading",
      "text": "The British Empire Kept Left"
    },
    {
      "type": "paragraph",
      "text": "Britain went the other way, formalizing left-hand travel in law during the 1700s and reinforcing it with the Highway Act of 1835. As the empire expanded, it exported the rule with it. That is why India, Australia, much of southern and eastern Africa, and a string of Caribbean nations still drive on the left today. Japan ended up on the left too — not as a British colony, but partly through British influence on its early railways and its own historical custom."
    },
    {
      "type": "heading",
      "text": "Where Each Side Lives Today"
    },
    {
      "type": "paragraph",
      "text": "Plot it on a map and the pattern is clear. Left-hand driving dominates the British-influenced world: the United Kingdom and Ireland, India, Pakistan, Bangladesh, Japan, Indonesia, Thailand, Australia, New Zealand, much of southern and eastern Africa, and most Caribbean islands. The Americas, continental Europe, China, Russia, and most of the Middle East and Africa drive on the right. The right-hand camp is the bigger of the two, covering roughly two-thirds of the world's countries and the large majority of its total road length."
    },
    {
      "type": "heading",
      "text": "When Countries Switched: Sweden's Dagen H"
    },
    {
      "type": "paragraph",
      "text": "Changing sides as a modern nation is a massive undertaking, and Sweden pulled it off in spectacular fashion. On 3 September 1967 — 'Dagen H', H for hogertrafik, or right-hand traffic — the entire country switched from left to right at five in the morning. It was planned for years, with repainted road markings, new signs covered until the big day, and a national publicity campaign. In the weeks that followed, accident rates actually dropped as drivers took extra care, before drifting back toward the long-run trend."
    },
    {
      "type": "heading",
      "text": "The Odd Cases"
    },
    {
      "type": "paragraph",
      "text": "A few places refuse to fit the pattern. The US Virgin Islands drive on the left even though the rest of the United States drives on the right, a leftover from their Danish colonial days. Myanmar switched from left to right in 1970, yet many of its cars are still right-hand-drive imports, so drivers there often sit on the kerb side — the opposite of the usual arrangement. And a handful of borders, like those between Thailand and its neighbors, need purpose-built crossover bridges where the carriageways physically swap sides so traffic can change over without a head-on tangle. These crossovers are oddly mesmerising from above — a neat little knot where two systems of the road quietly shake hands."
    }
  ]
};
